import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, Drawer, Typography, Button, Divider } from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PropTypes from "prop-types";
import MarksDrawer from "./MarksDrawer";

// React PDF Viewer imports
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

export default function EvaluationViewer({
  pdfUrl,
  questionPaperUrl,
  schemePaperUrl,
  onClose,
  valuation_script_detail_id,
  startTime, // ⏱ added
  onFinish,
}) {
  const [timeTaken, setTimeTaken] = useState(0);
  const [marksOpen, setMarksOpen] = useState(true);
  const [isFinal, setIsFinal] = useState(false);
  const [qpOpen, setQpOpen] = useState(false);
  const [scValOpen, setScValOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [qpBlobUrl, setQpBlobUrl] = useState(null);
  const [schemeBlobUrl, setSchemeBlobUrl] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [marks, setMarks] = useState({});
  const [examCourseId, setExamCourseId] = useState(null);
  const [visitedPages, setVisitedPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const saveTimerRef = useRef(null);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const roleId = user.core_user_roles?.[0]?.role_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const workerUrl = new URL("pdfjs-dist/build/pdf.worker.min.js", import.meta.url).toString();

  // ✅ Page navigation plugin (to allow jumpToPage)
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;

  // ✅ Toolbar plugin (disable open/download/print)
  const toolbarPluginInstance = toolbarPlugin({
    getFilePlugin: { enableDownload: false },
    printPlugin: { enablePrint: false },
    openPlugin: { enableOpenFile: false },
  });

  // ✅ Default layout plugin
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      slots: { open: () => <></>, download: () => <></>, print: () => <></> },
    },
  });

  // 🧩 Handle PDF page scrolling or change
  const handlePageChange = ({ currentPage }) => {
    const pageNum = currentPage + 1; // react-pdf-viewer uses 0-based
    setCurrentPage(pageNum);
    setVisitedPages((prev) => (prev.includes(pageNum) ? prev : [...prev, pageNum]));
  };

  // 🧩 Handle clicking on page number in drawer
  const handlePageSelect = (pageNum) => {
    jumpToPage(pageNum - 1); // plugin method, 0-based
    setCurrentPage(pageNum);
    setVisitedPages((prev) => (prev.includes(pageNum) ? prev : [...prev, pageNum]));
  };

  // 🧩 Load PDF blob
  useEffect(() => {
    if (!pdfUrl) return;

    const fetchPdf = async () => {
      try {
        const parts = pdfUrl.split(/[\\/]/);
        const timetableId = parts[3];
        const filename = parts[4];

        const response = await axios.get(
          `${API_BASE_URL}/api/script-valuation/pdf/${user.college_id}/${timetableId}/${filename}`,
          { responseType: "blob" }
        );
        const blobUrl = URL.createObjectURL(response.data);
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        console.error("Error fetching PDF:", err);
      }
    };
    fetchPdf();
    return () => pdfBlobUrl && URL.revokeObjectURL(pdfBlobUrl);
  }, [pdfUrl]);
  // QUESTION PAPER PDF
  useEffect(() => {
    if (!questionPaperUrl) return;

    const fetchQuestionPdf = async () => {
      try {
        // Extract timetableId from passed URL
        const parts = questionPaperUrl.split(/[\\/]/);
        const timetableId = parts[parts.length - 1]; // folder name
        const filename = parts[parts.length - 1]; // 1234.pdf

        const response = await axios.get(
          `${API_BASE_URL}/api/script-valuation/get-question-paper/${user.college_id}/${timetableId}`,
          { responseType: "blob" }
        );

        const blobUrl = URL.createObjectURL(response.data);
        setQpBlobUrl(blobUrl);
      } catch (err) {
        console.error("Error fetching Question Paper PDF:", err);
      }
    };

    fetchQuestionPdf();
    return () => qpBlobUrl && URL.revokeObjectURL(qpBlobUrl);
  }, [questionPaperUrl]);

  // SCHEME OF VALUATION PDF
  useEffect(() => {
    if (!schemePaperUrl) return;

    const fetchSchemePdf = async () => {
      try {
        // Extract timetableId
        const parts = schemePaperUrl.split(/[\\/]/);
        const timetableId = parts[parts.length - 1];
        const filename = parts[parts.length - 1];

        const response = await axios.get(
          `${API_BASE_URL}/api/script-valuation/get-scheme-paper/${user.college_id}/${timetableId}`,
          { responseType: "blob" }
        );

        const blobUrl = URL.createObjectURL(response.data);
        setSchemeBlobUrl(blobUrl);
      } catch (err) {
        console.error("Error fetching Scheme PDF:", err);
      }
    };

    fetchSchemePdf();
    return () => schemeBlobUrl && URL.revokeObjectURL(schemeBlobUrl);
  }, [schemePaperUrl]);

  const isInitialLoadRef = useRef(true);
  useEffect(() => {
    if (!valuation_script_detail_id) return;
    axios
      .get(
        `${API_BASE_URL}/api/script-valuation/questions-template/${user.client_id}/${user.college_id}/${valuation_script_detail_id}`
      )
      .then((res) => {
        const data = res.data?.data;
        if (data?.questions) {
          setQuestions(data.questions);
          setExamCourseId(data.exam_course_id);
        }
      })
      .catch((err) => console.error("Error fetching questions:", err));
  }, [valuation_script_detail_id]);

  // 🧩 Load existing marks
  useEffect(() => {
    if (!valuation_script_detail_id) return;
    const fetchMarks = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/script-valuation/${user.client_id}/getmarks`,
          { params: { valuation_script_detail_id } }
        );

        const markData = res.data?.data || [];
        const formatted = markData.reduce((acc, item) => {
          acc[item.template_question_id] = item.marks_awarded ?? "";
          return acc;
        }, {});
        setMarks(formatted);
        isInitialLoadRef.current = false;
      } catch (err) {
        console.error("Failed to load marks:", err);
      }
    };
    fetchMarks();
  }, [valuation_script_detail_id]);

  // 🧩 Save marks
  const isSavingRef = useRef(false);

  useEffect(() => {
    //if (roleId !== 6) return;
    if (isInitialLoadRef.current) return;
    if (!Object.keys(marks).length) return;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      // 🚫 double protection
      //if (roleId !== 6) return;
      if (isSavingRef.current) return;

      isSavingRef.current = true;
      if (roleId == 6) {
        handleSaveMarks(false).finally(() => {
          isSavingRef.current = false;
        });
      }
    }, 2000);

    return () => {
      clearTimeout(saveTimerRef.current);
    };
  }, [marks, roleId]);

  const handleSaveMarks = async (s = 0, g = 0, finalFlag = false) => {
    try {
      const endTime = Date.now();
      const diffMs = endTime - startTime;
      const diffMinutes = Math.round(diffMs / 60000);

      const markEntries = Object.entries(marks).map(([template_question_id, marks_awarded]) => {
        const q = questions.find((q) => q.template_question_id === parseInt(template_question_id));
        return {
          valuation_script_detail_id,
          exam_course_id: examCourseId,
          exam_template_id: q?.exam_template_id || 0,
          template_section_id: q?.template_section_id || 0,
          template_question_id: q?.template_question_id || 0,
          marks_awarded: marks_awarded || 0,
          remarks: "",
          sub_label: q?.sub_label || null,
          valuation_round: 0,
          created_by: user.username || user.user_email || "evaluator",
          modified_by: user.username || user.user_email || "evaluator",
        };
      });

      const payload = {
        valuation_script_detail_id,
        college_id: user.college_id,
        user_email: user.user_email,
        time_taken_for_valuation: diffMinutes,
        marks: markEntries,
        grandTotal: g,
        isFinal: finalFlag,
        roleId: roleId,
      };
      if (!markEntries.length) return;
      const response = await axios.post(
        `${API_BASE_URL}/api/script-valuation/${user.client_id}/savemarks`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`, // JWT
          },
        }
      );
      if (isFinal) {
        alert("Marks saved successfully!");
        if (onFinish) onFinish(diffMinutes);
      }
      return response;
    } catch (err) {
      console.error("Error saving marks:", err);
      alert("Failed to save marks.");
    }
  };

  const handleVerifyMarks = async () => {
    try {
      const payload = {
        valuation_script_detail_id,
        evaluation_status: "VERIFIED",
        modified_by: user.user_email || "verifier",
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/script-valuation/${user.client_id}/verify`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`, // JWT
          },
        }
      );

      alert("Marks verified successfully!");
      return response;
      //if (onFinish) onFinish(); // refresh / close drawer
    } catch (err) {
      console.error("Error verifying marks:", err);
      alert(err.response?.data?.error || "Failed to verify marks.");
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", position: "relative" }}>
      {/* Toggle Buttons */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          right: 10, // ✅ move to right
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "green",
            borderColor: "white",
            "&:hover": { borderColor: "#90caf9", color: "#90caf9" },
          }}
          onClick={() => setMarksOpen(!marksOpen)}
        >
          Marks
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setQpOpen(!qpOpen)}
          sx={{
            color: "blue",
            borderColor: "white",
            "&:hover": { borderColor: "#90caf9", color: "#90caf9" },
          }}
        >
          Q.Paper
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setScValOpen(!scValOpen)}
          sx={{
            color: "red",
            borderColor: "white",
            "&:hover": { borderColor: "#90caf9", color: "#90caf9" },
          }}
        >
          Scheme
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{
            color: "red",
            borderColor: "white",
            "&:hover": { borderColor: "#90caf9", color: "#90caf9" },
          }}
        >
          Close
        </Button>
      </Box>
      <MarksDrawer
        open={marksOpen}
        onClose={() => setMarksOpen(false)}
        questions={questions}
        marks={marks}
        setMarks={setMarks}
        setIsFinal={setIsFinal}
        onSave={(s, g, finalFlag) => handleSaveMarks(s, g, finalFlag)}
        onVerify={handleVerifyMarks}
        visitedPages={visitedPages}
        currentPage={currentPage}
        onPageSelect={handlePageSelect}
      />

      {/* 🧾 Right Drawer: Question Paper */}
      <Drawer
        anchor="right"
        open={qpOpen}
        onClose={() => setQpOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: "50%", p: 2 } }}
      >
        <Typography variant="h6" gutterBottom>
          Question Paper
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <iframe
          src={qpBlobUrl}
          title="Question Paper"
          width="100%"
          height="90%"
          style={{ border: "none" }}
        />
      </Drawer>
      <Drawer
        anchor="right"
        open={scValOpen}
        onClose={() => setScValOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: "50%", p: 2 } }}
      >
        <Typography variant="h6" gutterBottom>
          Scheme of Valuation
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <iframe
          src={schemeBlobUrl}
          title="Scheme of Valuation"
          width="100%"
          height="90%"
          style={{ border: "none" }}
        />
      </Drawer>
      {/* 📘 PDF Viewer */}
      <Box
        sx={{
          flexGrow: 1,
          textAlign: "center",
          pt: 2, // ✅ no top padding
          px: 5, // optional left/right padding
          pb: 1,
          "& .rpv-core__inner-pages": {
            paddingRight: 0,
            marginRight: 0,
          },
          "& .rpv-core__page-layer": {
            marginRight: 0,
          },
          "& .rpv-core__page": {
            boxShadow: "none",
          },
        }}
      >
        {pdfBlobUrl ? (
          <Worker workerUrl={workerUrl}>
            <Viewer
              fileUrl={pdfBlobUrl}
              plugins={[toolbarPluginInstance, pageNavigationPluginInstance]}
              onPageChange={handlePageChange}
            />
          </Worker>
        ) : (
          <Typography>Loading PDF...</Typography>
        )}

        {/*  {/* Footer Controls  
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditNoteIcon />}
            onClick={handleSaveMarks}
          >
            Save Marks
          </Button>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </Box> */}
      </Box>
    </Box>
  );
}

EvaluationViewer.propTypes = {
  pdfUrl: PropTypes.string.isRequired,
  questionPaperUrl: PropTypes.string.isRequired,
  schemePaperUrl: PropTypes.string.isRequired,
  valuation_script_detail_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

  // ✅ ADD THESE TWO
  startTime: PropTypes.number.isRequired, // Date.now() timestamp
  onFinish: PropTypes.func, // callback after save

  onClose: PropTypes.func.isRequired,
};
